import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { pmt2Service } from '../../services/pmt2Service';
import { PMT2SpreadsheetConfig, PMT2SyncStatus } from '../../types/pmt2Types';
import { useToast } from '../../components/ui/use-toast';
import { Trash2, ExternalLink, Edit2, Save, X } from 'lucide-react';
import { CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Section for managing PMT3 spreadsheet configurations
 */
const PMT3ManageCompanies: React.FC = () => {
  const { toast } = useToast();
  const [spreadsheetId, setSpreadsheetId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [configs, setConfigs] = useState<PMT2SpreadsheetConfig[]>([]);
  const [syncStatus, setSyncStatus] = useState<PMT2SyncStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [checkResult, setCheckResult] = useState<{ accessible: boolean; message: string } | null>(null);
  
  // State for confirmation dialog
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [configToRemove, setConfigToRemove] = useState<{ id: string, companyName: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCompanyName, setEditCompanyName] = useState<string>('');

  // Load configurations on mount
  useEffect(() => {
    loadConfigs();
    loadSyncStatus();
  }, []);

  // Load spreadsheet configurations
  const loadConfigs = async () => {
    try {
      setLoading(true);
      const configsData = await pmt2Service.getSpreadsheetConfigs();
      setConfigs(configsData);
    } catch (err) {
      console.error('Error loading configurations:', err);
      const errorMessage = 'Failed to load spreadsheet configurations';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 5000
      });
      
      // If it's a permission error, offer to retry
      if (err instanceof Error && (err.message.includes('Access denied') || err.message.includes('403'))) {
        setError(`${errorMessage}\n\nThis appears to be a permissions issue. Google may need a few minutes to propagate permission changes.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load sync status
  const loadSyncStatus = async () => {
    try {
      const status = await pmt2Service.getSyncStatus();
      setSyncStatus(status as PMT2SyncStatus);
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  // Check spreadsheet access
  const checkSpreadsheetAccess = async () => {
    if (!spreadsheetId.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a Spreadsheet ID to check',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    setIsChecking(true);
    setCheckResult(null);
    setError(null);
    
    try {
      console.log(`Checking access for spreadsheet ID: ${spreadsheetId}`);
      const result = await pmt2Service.checkSpreadsheetAccess(spreadsheetId);
      
      if (result.accessible) {
        setCheckResult({
          accessible: true,
          message: `Spreadsheet "${result.details?.spreadsheetTitle}" is accessible and contains the required "Client Bundle SKUs" sheet.`
        });
        toast({
          title: 'Success',
          description: 'Spreadsheet is accessible with the correct permissions and sheet name.',
          duration: 5000
        });
      } else {
        setCheckResult({
          accessible: false,
          message: result.error || 'Unknown error occurred when checking spreadsheet access.'
        });
        toast({
          title: 'Access Check Failed',
          description: result.error || 'Unknown error occurred when checking spreadsheet access.',
          variant: 'destructive',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error checking spreadsheet access:', error);
      setCheckResult({
        accessible: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred when checking spreadsheet access.'
      });
    } finally {
      setIsChecking(false);
    }
  };

  // Retry adding spreadsheet with a delay
  const retryAddSpreadsheet = async () => {
    if (retryCount >= 3) {
      setError("Maximum retry attempts reached (3/3). Please check the spreadsheet permissions and try again later.");
      setIsRetrying(false);
      setRetryCount(0);
      return;
    }

    // Increment retry count first
    const currentRetry = retryCount + 1;
    setRetryCount(currentRetry);
    setIsRetrying(true);
    setError(`Retrying... (Attempt ${currentRetry}/3). Waiting for Google's permission changes to propagate.`);
    
    // Wait for 10 seconds before retrying
    await new Promise(resolve => setTimeout(resolve, 10000));
    
    try {
      console.log(`Retry attempt ${currentRetry} for spreadsheet config - ID: ${spreadsheetId}, Company: ${companyName}`);
      await pmt2Service.addSpreadsheetConfig(spreadsheetId, companyName);
      
      toast({
        title: 'Success',
        description: `Added spreadsheet configuration for ${companyName} after retry`,
        duration: 5000
      });
      
      // Reset form
      setSpreadsheetId('');
      setCompanyName('');
      setIsRetrying(false);
      setRetryCount(0);
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error(`Retry ${currentRetry} failed:`, error);
      
      // If we haven't reached max retries, try again
      if (currentRetry < 3) {
        // Wait a bit before trying again
        setTimeout(() => {
          retryAddSpreadsheet();
        }, 1000);
      } else {
        setError(`All retry attempts failed (${currentRetry}/3). Please check the spreadsheet permissions and try again later.`);
        setIsRetrying(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRetryCount(0);
    setIsRetrying(false);
    
    if (!spreadsheetId.trim() || !companyName.trim()) {
      setError('Spreadsheet ID and Company Name are required');
      toast({
        title: 'Validation Error',
        description: 'Spreadsheet ID and Company Name are required',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    // Check for duplicates
    if (configs.some(config => config.spreadsheetId === spreadsheetId)) {
      setError('This Spreadsheet ID is already in use for another company.');
      toast({
        title: 'Duplicate Error',
        description: 'This Spreadsheet ID is already configured. Each spreadsheet can only be used once.',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    // Require access check
    if (checkResult === null || !checkResult.accessible) {
      setError('Please check access to the spreadsheet first and ensure it succeeds.');
      toast({
        title: 'Access Check Required',
        description: 'Click "Check Access" and ensure the spreadsheet is accessible before adding.',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    try {
      setLoading(true);
      console.log(`Submitting spreadsheet config - ID: ${spreadsheetId}, Company: ${companyName}`);
      await pmt2Service.addSpreadsheetConfig(spreadsheetId, companyName);
      
      toast({
        title: 'Success',
        description: `Added spreadsheet configuration for ${companyName}`,
        duration: 5000
      });
      
      // Reset form
      setSpreadsheetId('');
      setCompanyName('');
      setCheckResult(null);
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error('Error adding spreadsheet configuration:', error);
      
      // Extract error message for display
      let errorMessage = 'Failed to add spreadsheet configuration';
      
      let isPermissionError = false;
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        errorMessage = error.message;
      } else {
        console.error('Unknown error type:', error);
      }
      
      // Set error for display in the UI
      setError(errorMessage);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle configuration removal
  const handleRemoveConfig = (configId: string, companyName: string) => {
    // Open confirmation dialog
    setConfigToRemove({ id: configId, companyName });
    setIsConfirmOpen(true);
  };
  
  // Handle confirmation dialog confirm
  const handleConfirmRemove = async () => {
    if (!configToRemove) return;
    
    try {
      setLoading(true);
      await pmt2Service.removeSpreadsheetConfig(configToRemove.id);
      
      toast({
        title: 'Success',
        description: `Removed spreadsheet configuration for ${configToRemove.companyName}`,
        duration: 5000
      });
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error('Error removing configuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove spreadsheet configuration',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
    
    // Reset state
    setConfigToRemove(null);
    setIsConfirmOpen(false);
  };

  // Edit functions
  const handleEditCompany = (config: PMT2SpreadsheetConfig) => {
    setEditingId(config.id);
    setEditCompanyName(config.companyName);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditCompanyName('');
  };

  const handleSaveEdit = async (configId: string) => {
    if (!editCompanyName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Company name is required',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    // Check for duplicates (case insensitive)
    if (configs.some(c => c.id !== configId && c.companyName.toLowerCase() === editCompanyName.toLowerCase())) {
      toast({
        title: 'Duplicate Error',
        description: 'This company name is already in use.',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    try {
      setLoading(true);
      await pmt2Service.updateSpreadsheetConfig(configId, editCompanyName);
      
      toast({
        title: 'Success',
        description: `Updated company name to ${editCompanyName}`,
        duration: 5000
      });

      // Reload configurations
      await loadConfigs();
      setEditingId(null);
      setEditCompanyName('');
    } catch (error) {
      console.error('Error updating company name:', error);
      toast({
        title: 'Error',
        description: 'Failed to update company name',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };




  // Format timestamp
  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Never';
    
    try {
      return new Date(timestamp.toMillis()).toLocaleString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Manage Companies</CardTitle>
          <CardDescription>
            Manage Google Sheets spreadsheets for Amazon seller data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Add New Configuration */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold">Add New Spreadsheet</h3>
          
          {error && (
            <Alert variant={isRetrying ? "default" : "destructive"} className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
              
              {error.includes('Access denied') && !isRetrying && (
                <div className="mt-4 flex flex-col gap-2">
                  <Button 
                    onClick={retryAddSpreadsheet} 
                    variant="outline" 
                    size="sm"
                    disabled={isRetrying}
                  >
                    Retry (Wait for permissions to propagate)
                  </Button>
                  <div className="text-sm text-muted-foreground mt-2">
                    <p>If retries fail, please check:</p>
                    <ol className="list-decimal pl-5 mt-1 space-y-1">
                      <li>The spreadsheet is shared with <strong>jls-apps@jls-apps.iam.gserviceaccount.com</strong></li>
                      <li>The service account has <strong>Editor</strong> access (not just Viewer)</li>
                      <li>The sheet is named exactly <strong>"Client Bundle SKUs"</strong></li>
                      <li>Try creating a new test spreadsheet with a simple structure</li>
                    </ol>
                  </div>
                </div>
              )}
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="spreadsheetId">Google Sheets Spreadsheet ID</Label>
              <Input
                id="spreadsheetId"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                placeholder="Enter spreadsheet ID"
                disabled={loading}
              />
              <p className="text-sm text-muted-foreground">
                The ID from the spreadsheet URL
              </p>
              <div className="mt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={checkSpreadsheetAccess}
                  disabled={isChecking || !spreadsheetId.trim()}
                  className="flex items-center gap-2"
                >
                  {isChecking ? 'Checking...' : 'Check Access'}
                </Button>
              </div>
              
              {checkResult && (
                <div className={`mt-2 p-2 rounded-md text-sm flex items-start gap-2 ${
                  checkResult.accessible 
                    ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                    : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}>
                  {checkResult.accessible 
                    ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> 
                    : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                  <span>{checkResult.message}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name"
                disabled={loading}
              />
              <p className="text-sm text-muted-foreground">
                A unique name to identify this seller
              </p>
            </div>
          </div>
          
          <Button
            type="submit"
            disabled={loading || checkResult === null || !checkResult.accessible}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loading ? 'Adding...' : 'Add Spreadsheet'}
          </Button>
          {(checkResult === null || !checkResult.accessible) && (
            <p className="text-sm text-red-500 mt-2">
              {checkResult === null ? 'Please click "Check Access" first.' : 'Please resolve the access issues before adding this spreadsheet.'}
            </p>
          )}
        </form>
        
        <Separator className="my-6" />
        
        {/* Sync Status */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Sync Status</h3>
          
          {syncStatus ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Last Sync</div>
                  <div className="text-base font-medium">
                    {syncStatus.lastSync ? formatTimestamp(syncStatus.lastSync) : 'Never'}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Status</div>
                  <div className={`text-base font-medium ${
                    syncStatus.inProgress
                      ? 'text-blue-500'
                      : syncStatus.lastSyncSuccess
                        ? 'text-green-500'
                        : 'text-red-500'
                  }`}>
                    {syncStatus.inProgress
                      ? 'In Progress'
                      : syncStatus.lastSyncSuccess
                        ? 'Success'
                        : 'Failed'}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Companies</div>
                  <div className="text-base font-medium">
                    {(syncStatus as any)?.totalSpreadsheetsProcessed || 0}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Products</div>
                  <div className="text-base font-medium">
                    {syncStatus.processedProducts || 0}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Changes</div>
                  <div className="text-base font-medium">
                    {syncStatus.totalChanges || 0}
                  </div>
                </div>
              </div>
              
              {syncStatus.error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTitle>Sync Error</AlertTitle>
                  <AlertDescription>{syncStatus.error}</AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">No sync information available</p>
          )}
        </div>
        
        <Separator className="my-6" />
        
        {/* Configurations List */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Configured Spreadsheets</h3>
          
          {configs.length === 0 ? (
            <p className="text-muted-foreground">No spreadsheets configured yet</p>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Spreadsheet ID</TableHead>
                    <TableHead>Last Sync</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell>
                        {editingId === config.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editCompanyName}
                              onChange={(e) => setEditCompanyName(e.target.value)}
                              className="w-48"
                              disabled={loading}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleSaveEdit(config.id)}
                              disabled={loading}
                              className="h-8 w-8 p-0"
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelEdit}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between w-full">
                            <span className="font-medium">{config.companyName}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditCompany(config)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm truncate max-w-[150px]">{config.spreadsheetId}</span>
                          <a 
                            href={`https://docs.google.com/spreadsheets/d/${config.spreadsheetId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>{formatTimestamp(config.lastSyncAt)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          config.lastSyncStatus === 'success' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                            : config.lastSyncStatus === 'error'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {config.lastSyncStatus === 'success' 
                            ? 'Success' 
                            : config.lastSyncStatus === 'error' 
                              ? 'Error' 
                              : 'Pending'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1">
                          {editingId !== config.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveConfig(config.id, config.companyName)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      {/* Confirmation Dialog */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the spreadsheet configuration for {configToRemove?.companyName}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfigToRemove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </Card>
    </div>
  );
};

export default PMT3ManageCompanies;